import { useRef, useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { marked } from "marked";
import html2pdf from "html2pdf.js";
import HtmlDocx from "html-docx-js/dist/html-docx";
import { saveAs } from "file-saver";
import { useGlobalState } from "./GlobalStateContext";
import "./TextSummarization.css";
import { secureStorage } from "./utils/secureStorage";
import { toast } from "react-toastify"; // if you're using react-toastify


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

const MAX_FILE_SIZE = 200 * 1024; // 100 KB in bytes

const TextSummarization = () => {
  const {
    sessionId,
    summaries,
    setSummaries,
    selectedFiles,
    setSelectedFiles,
    token,
  } = useGlobalState();

  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const savedFiles = secureStorage.getItem("selectedFiles");
    const savedSummaries = secureStorage.getItem("summaries");
    const isUploading = secureStorage.getItem("uploading");

    if (token) {
      sessionStorage.setItem("wasLoggedIn", "true");
      if (savedFiles) setSelectedFiles(JSON.parse(savedFiles));
      if (savedSummaries) setSummaries(JSON.parse(savedSummaries));
      if (isUploading) setLoading(true);
    } else {
      const wasLoggedIn = sessionStorage.getItem("wasLoggedIn") === "true";

      if (wasLoggedIn) {
        secureStorage.removeItem("selectedFiles");
        secureStorage.removeItem("summaries");
        secureStorage.removeItem("uploading");
        setSelectedFiles([]);
        setSummaries([]);
        sessionStorage.removeItem("wasLoggedIn");
      }
    }
  }, [token, setSelectedFiles, setSummaries]);

  const handleFileChange = (event) => {
    const filesArray = [...event.target.files];
    const filtered = [];

    for (const file of filesArray) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large. Max size allowed is 200 KB.`);
      } else {
        filtered.push(file);
      }
    }
    


    setSelectedFiles(filtered);
    secureStorage.setItem("selectedFiles", JSON.stringify(filtered));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error(`Please select at least one file to summarize.`);
      // alert("Please select at least one file to summarize.");
      return;
    }

    setLoading(true);
    secureStorage.setItem("uploading", "true");

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("file", file));

    try {
      const response = await fetch(`${API_BASE_URL}/summarize/`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      const result = await response.json();
      if (result.filename && result.summary) {
        const newSummaries = [
          { filename: result.filename, summary: result.summary, sessionId },
          ...summaries,
        ];

        setSummaries(newSummaries);
        secureStorage.setItem("summaries", JSON.stringify(newSummaries));

        setSelectedFiles([]);
        secureStorage.removeItem("selectedFiles");
        secureStorage.removeItem("uploading");
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        alert("Unexpected response from the server.");
      }
    } catch (error) {
      alert("Failed to summarize documents. Please try again.");
    } finally {
      setLoading(false);
      secureStorage.removeItem("uploading");
    }
  };

  const markdownToHTML = (markdown) => marked.parse(markdown);

  const downloadAsPDF = (filename, summaryMarkdown) => {
    const htmlContent = markdownToHTML(summaryMarkdown);
    const container = document.createElement("div");
    container.innerHTML = `<h1>${filename}</h1>${htmlContent}`;
    document.body.appendChild(container);

    html2pdf()
      .set({
        margin: 10,
        filename: `${filename}_summary.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      })
      .from(container)
      .save()
      .then(() => document.body.removeChild(container));
  };

  const downloadAsWord = (filename, summaryMarkdown) => {
    const htmlContent = markdownToHTML(summaryMarkdown);
    const fullHTML = `
      <html><head><meta charset="utf-8"></head><body><h1>${filename}</h1>${htmlContent}</body></html>
    `;
    const converted = HtmlDocx.asBlob(fullHTML);
    saveAs(converted, `${filename}_summary.docx`);
  };

  return (
    <div className="text-summarization-container">
      <h2 className="summarization-title">Document Summarization</h2>

      <div className="file-upload-container">
        <input
          type="file"
          ref={fileInputRef}
          multiple
          accept=".pdf, .txt, .csv, .xlsx, .docx"
          onChange={handleFileChange}
          className="file-input"
        />
        <button className="upload-button" onClick={handleUpload} disabled={loading}>
          {loading ? "Summarizing..." : "Upload & Summarize"}
        </button>
      </div>

      <div className="summaries-container">
        {summaries.length > 0 ? (
          summaries.map((summaryObj, index) => (
            <div key={index} className="summary-card">
              <h3>{summaryObj.filename}</h3>
              <div className="summary-text">
                <ReactMarkdown>{summaryObj.summary}</ReactMarkdown>
              </div>
              <div className="summary-actions">
                <button onClick={() => downloadAsPDF(summaryObj.filename, summaryObj.summary)}>
                  Download PDF
                </button>
                <button onClick={() => downloadAsWord(summaryObj.filename, summaryObj.summary)}>
                  Download Word
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="no-summary">No summaries available. Upload a file to generate a summary.</p>
        )}
      </div>
    </div>
  );
};

export default TextSummarization;
